<script setup lang="ts">
// YfbToast.vue — 自研轻量瞬态提示浮层（2026-09-09 替换 vue-sonner；2026-09-09 交互优化）
// 居中偏上 40% fixed + Teleport 到 body；纯被动展示：统一 3s 自动消失，无关闭按钮/hover 暂停。
// 样式严格走 design tokens（玻璃态 + 信号色 + 霓虹光晕），不做全局 token 变更。
// 状态与生命周期见 src/utils/toast.ts；调用入口见 src/utils/feedback.ts。
import { toasts } from '@/utils/toast'
import YfbIcon from '@/components/YfbIcon.vue'
</script>

<template>
  <Teleport to="body">
    <div class="yfb-toast-host" aria-live="polite" aria-atomic="false">
      <div
        v-for="item in toasts"
        :key="item.id"
        class="yfb-toast"
        :class="{ 'yfb-toast--leaving': item.leaving }"
        :data-type="item.type"
        :role="item.type === 'error' || item.type === 'warning' ? 'alert' : 'status'"
      >
        <span class="yfb-toast__icon" aria-hidden="true">
          <YfbIcon
            :name="
              item.type === 'success'
                ? 'ph:check-circle-bold'
                : item.type === 'error'
                  ? 'ph:x-circle-bold'
                  : item.type === 'warning'
                    ? 'ph:warning-bold'
                    : 'lucide:info'
            "
            :size="18"
          />
        </span>
        <p class="yfb-toast__msg">{{ item.message }}</p>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.yfb-toast-host {
  position: fixed;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  width: min(480px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-2, 8px);
  pointer-events: none;
}

.yfb-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--yfb-sp-3, 12px);
  width: 100%;
  padding: var(--yfb-sp-3, 12px) var(--yfb-sp-4, 16px);
  border-radius: var(--yfb-radius-lg, 12px);
  background: var(--yfb-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  border: 1px solid var(--yfb-glass-border);
  font-family: var(--yfb-font-ui);
  box-shadow: 0 0 18px var(--yfb-glow), 0 8px 24px var(--yfb-drop);
  transition:
    opacity var(--yfb-anim-base, 200ms) ease,
    transform var(--yfb-anim-base, 200ms) ease;
  animation: yfb-toast-in var(--yfb-anim-base, 200ms) ease both;
}

/* 退场：淡出 + 轻微下移（由 toast.ts 置 leaving 后 200ms 出队） */
.yfb-toast--leaving {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

/* 进场：淡入 + 轻上移 + 缩放，契合居中浮层 */
@keyframes yfb-toast-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 类型信号色（边框 + 图标 + 光晕，全部走 color-mix 于 design tokens） */
.yfb-toast[data-type='success'] {
  border-color: color-mix(in srgb, var(--yfb-signal-ok) 45%, var(--yfb-glass-border));
  box-shadow:
    0 0 18px color-mix(in srgb, var(--yfb-signal-ok) 30%, transparent),
    0 8px 24px var(--yfb-drop);
}
.yfb-toast[data-type='success'] .yfb-toast__icon {
  color: var(--yfb-signal-ok);
}

.yfb-toast[data-type='error'] {
  border-color: color-mix(in srgb, var(--yfb-signal-danger) 45%, var(--yfb-glass-border));
  box-shadow:
    0 0 18px color-mix(in srgb, var(--yfb-signal-danger) 30%, transparent),
    0 8px 24px var(--yfb-drop);
}
.yfb-toast[data-type='error'] .yfb-toast__icon {
  color: var(--yfb-signal-danger);
}

.yfb-toast[data-type='warning'] {
  border-color: color-mix(in srgb, var(--yfb-signal-warning) 45%, var(--yfb-glass-border));
  box-shadow:
    0 0 18px color-mix(in srgb, var(--yfb-signal-warning) 30%, transparent),
    0 8px 24px var(--yfb-drop);
}
.yfb-toast[data-type='warning'] .yfb-toast__icon {
  color: var(--yfb-signal-warning);
}

.yfb-toast[data-type='info'] {
  border-color: color-mix(in srgb, var(--yfb-signal-info) 45%, var(--yfb-glass-border));
  box-shadow:
    0 0 18px color-mix(in srgb, var(--yfb-signal-info) 30%, transparent),
    0 8px 24px var(--yfb-drop);
}
.yfb-toast[data-type='info'] .yfb-toast__icon {
  color: var(--yfb-signal-info);
}

.yfb-toast__icon {
  flex: none;
  display: inline-flex;
  margin-top: 2px;
}

.yfb-toast__msg {
  flex: 1;
  margin: 0;
  font-size: var(--yfb-fs-sm, 14px);
  line-height: 1.6;
  color: var(--yfb-ink);
  overflow-wrap: anywhere;
}
</style>